import {
  FC,
  forwardRef,
  ReactElement,
  Ref,
  SetStateAction,
  useEffect,
  useState,
} from "react";
import {
  Avatar,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Slide,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { TransitionProps } from "@mui/material/transitions";
import { Edit, Sports, Sync, Videocam } from "@mui/icons-material";
import dayjs from "dayjs";
import { useSearchParams } from "react-router-dom";
import { SamsScope, samsScopes } from "@/models/samsVbl";
import { PdfData } from "@/models/pdfData";
import { useResponsiveContext } from "@context/ResponsiveContext";
import { loadGameData, tokenNeeded } from "@/services/samsService";
import { millisToDateTime } from "@/services/dateTimeService";
import { Views } from "@/models/gameGeneral";
import { useGameDataContext } from "@context/GameDataContext";
import GameSelectionDropdown, {
  GameSelectionDropdownOption,
} from "@components/controls/GameSelectionDropdown";
import DateTimePicker from "@components/DateTimePicker";

const Transition = forwardRef(function Transition(
  props: TransitionProps & {
    children: ReactElement<any, any>;
  },
  ref: Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...{ ...props, key: undefined }} />;
});

const TeamDataInput = ({
  longName,
  setLongName,
  shortName,
  setShortName,
  title,
  teamId,
  missingInputIds,
  logoUrl,
}: {
  title: string;
  longName: string;
  setLongName: (value: SetStateAction<string>) => void;
  shortName: string;
  setShortName: (value: SetStateAction<string>) => void;
  teamId: "team1" | "team2";
  missingInputIds: string[];
  logoUrl?: string;
}) => {
  return (
    <Card>
      <CardContent>
        <Grid container columns={3}>
          <Grid size={3}>
            <Typography variant={"h6"} align={"center"}>
              {title}
            </Typography>
          </Grid>
          <Grid size={3}>
            <TextField
              autoFocus
              required
              margin="dense"
              id={`${teamId}LongName`}
              label="Voller Name"
              type="text"
              fullWidth
              error={missingInputIds.includes(`${teamId}LongName`)}
              value={longName}
              onChange={(event) => setLongName(event.target.value)}
              variant="outlined"
            />
          </Grid>
          <Grid size={1} alignContent={"center"}>
            {logoUrl && <Avatar alt="Team A" src={logoUrl} />}
          </Grid>
          <Grid size={2}>
            <TextField
              autoFocus
              required
              margin="dense"
              id={`${teamId}ShortName`}
              label="Abkürzung"
              type="text"
              fullWidth
              error={missingInputIds.includes(`${teamId}ShortName`)}
              value={shortName}
              onChange={(event) => setShortName(event.target.value)}
              variant="outlined"
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

const teamAvatarSx = {
  height: "10vw",
  width: "10vw",
  maxWidth: "150px",
  maxHeight: "150px",
};

const CreateGameWizard: FC<{
  open: boolean;
  handleClose: () => void;
  onCreateGame: (
    game: Pick<PdfData, "scope" | "samsId">,
    destination: Views,
  ) => void;
}> = ({ open, handleClose, onCreateGame }) => {
  const [searchParams] = useSearchParams();
  const { onMobile } = useResponsiveContext();
  const { storeGame, useGame } = useGameDataContext();

  const [samsId, setSamsId] = useState("");
  const [samsIdError, setSamsIdError] = useState(false);

  // describes which fields are currently empty
  const [emptyInputIds, setEmptyInputIds] = useState<string[]>([]);
  // describes which fields are missing after submit was pressed
  const [missingInputIds, setMissingInputIds] = useState<string[]>([]);

  const [authToken, setAuthToken] = useState<string>();
  const [gameStartDateTime, setGameStartDateTime] = useState(0);
  const [team1LongName, setTeamALongName] = useState("");
  const [team1ShortName, setTeamAShortName] = useState("");
  const [team1LogoUrl, setTeamALogoUrl] = useState<string>();
  const [team2LongName, setTeamBLongName] = useState("");
  const [team2ShortName, setTeamBShortName] = useState("");
  const [team2LogoUrl, setTeamBLogoUrl] = useState<string>();

  const [loadingGames, setLoadingGames] = useState(true);
  const [dataLoadedFromSams, setDataLoadedFromSams] = useState<PdfData>();
  const [gameOptions, setGameOptions] = useState<GameSelectionDropdownOption[]>(
    [],
  );
  const [games, setGames] = useState<{ [key in SamsScope]?: PdfData[] }>({});
  const [scope, setScope] = useState<SamsScope>(
    samsScopes.find((s) => searchParams.get("scope") === s) || "vbl",
  );
  const [state, setState] = useState<"dataInput" | "modeSelector">("dataInput");

  useEffect(() => {
    const data = {
      gameStartDateTime,
      team1ShortName,
      team1LongName,
      team2ShortName,
      team2LongName,
      samsId,
      authToken: tokenNeeded(dataLoadedFromSams?.scope)
        ? authToken
        : "not needed",
    };
    setEmptyInputIds(
      Object.entries(data)
        .filter(([, value]) => !value)
        .map(([key]) => key),
    );
  }, [
    samsId,
    gameStartDateTime,
    team1LongName,
    team1ShortName,
    team2LongName,
    team2ShortName,
    authToken,
  ]);

  useEffect(() => {
    setSamsIdError(
      !!samsId &&
        !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(
          samsId,
        ),
    );
  }, [samsId]);

  const updateGameOptions = async (scope: SamsScope) => {
    setLoadingGames(true);

    const downloadGameData = (scope: SamsScope) =>
      loadGameData({ scope }).then((data) => {
        setGames((prevGames) => ({ ...prevGames, [scope]: data }));
        return data;
      });

    const gameData = games[scope] || (await downloadGameData(scope));

    const currentDateTime = Date.now() - 30 * 60 * 1000;

    setGameOptions(
      gameData
        .sort((a, b) => {
          if (
            a.gameData.gameStartDateTime < currentDateTime &&
            b.gameData.gameStartDateTime >= currentDateTime
          ) {
            return 1;
          } else if (
            b.gameData.gameStartDateTime < currentDateTime &&
            a.gameData.gameStartDateTime >= currentDateTime
          ) {
            return -1;
          } else {
            return a.gameData.gameStartDateTime - b.gameData.gameStartDateTime;
          }
        })
        .map((game) => ({
          ...game,
          prettyDate: millisToDateTime(game.gameData.gameStartDateTime),
        })),
    );

    setLoadingGames(false);
  };

  useEffect(() => {
    if (scope) {
      updateGameOptions(scope);
    }
  }, [scope]);

  const loadDataFromSams = async () => {
    let data = games[scope]?.find((game) => game.samsId === samsId);
    if (!data) {
      data = await loadGameData({ samsId, scope });
    }
    if (data) {
      const { gameStartDateTime, team1, team2 } = data.gameData;
      setGameStartDateTime(gameStartDateTime);
      setTeamALongName(team1.longName);
      setTeamAShortName(team1.shortName);
      setTeamALogoUrl(team1.iconUrl);
      setTeamBLongName(team2.longName);
      setTeamBShortName(team2.shortName);
      setTeamBLogoUrl(team2.iconUrl);
      setDataLoadedFromSams(data);
    }
  };

  const handleCreate = () => {
    const data = {
      gameStartDateTime,
      team1ShortName,
      team1LongName,
      team2ShortName,
      team2LongName,
      samsId,
    };
    const inputMissing = Object.entries(data)
      .filter(([, value]) => !value)
      .map(([key]) => key);
    if (inputMissing.length <= 0) {
      setState("modeSelector");
    } else {
      setMissingInputIds(inputMissing);
    }
  };

  const handleSubmit = (destination: Views) => {
    const newGame = {
      samsId,
      authToken,
      scope,
      gameData: {
        gameStartDateTime,
        scoresheetPdfUrl: "",
        team1: {
          shortName: team1ShortName,
          longName: team1LongName,
          clubCode:
            dataLoadedFromSams?.gameData.team1.clubCode ||
            team1ShortName.substring(0, 3),
          iconUrl: team1LogoUrl,
        },
        team2: {
          shortName: team2ShortName,
          longName: team2LongName,
          clubCode:
            dataLoadedFromSams?.gameData.team2.clubCode ||
            team2ShortName.substring(0, 3),
          iconUrl: team2LogoUrl,
        },
      },
      challenges: [],
      challengesUsed: {},
    };
    storeGame(newGame);
    useGame(samsId, scope);
    onCreateGame(newGame, destination);
  };

  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      fullWidth={true}
      fullScreen={onMobile}
      maxWidth={"sm"}
      keepMounted
      onClose={handleClose}
      aria-describedby="alert-dialog-slide-description"
    >
      <DialogTitle>
        {`${state === "dataInput" ? "Neues Spiel erstellen" : "Modus auswählen"}`}
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-slide-description"></DialogContentText>
        <Grid container spacing={2} columns={12}>
          {state === "dataInput" ? (
            <>
              <Grid size={12}>
                <ToggleButtonGroup
                  color={"primary"}
                  exclusive
                  onChange={(e, value) => value && setScope(value)}
                  value={scope}
                  fullWidth
                >
                  <ToggleButton value="vbl">VBL</ToggleButton>
                  <ToggleButton value="dvv">DVV</ToggleButton>
                  <ToggleButton value="bvv">BVV</ToggleButton>
                </ToggleButtonGroup>
              </Grid>
              <Grid size={12}>
                <GameSelectionDropdown
                  loadingGames={loadingGames}
                  games={gameOptions}
                  samsIdError={samsIdError}
                  onGameSelected={(samsId: string) => setSamsId(samsId)}
                />
              </Grid>
              <Grid
                size={6}
                justifyContent={"center"}
                alignItems={"center"}
                display={"flex"}
              >
                <Button
                  startIcon={<Sync />}
                  variant="contained"
                  disabled={!samsId || samsIdError}
                  onClick={loadDataFromSams}
                >
                  Daten laden
                </Button>
              </Grid>
              <Grid
                size={6}
                justifyContent={"center"}
                alignItems={"center"}
                display={"flex"}
              >
                <Button startIcon={<Edit />} variant="contained">
                  Manuell anlegen
                </Button>
              </Grid>
              {!!gameStartDateTime && (
                <>
                  <Grid size={12}>
                    <Typography variant={"h6"} align={"center"}>
                      Spiel
                    </Typography>
                  </Grid>
                  <Grid size={5} alignContent={"start"}>
                    <DateTimePicker
                      sx={{ my: 1 }}
                      label={"Startzeit"}
                      value={dayjs(gameStartDateTime)}
                      onChange={(event) => {
                        event && setGameStartDateTime(event?.millisecond);
                      }}
                    />
                  </Grid>
                  {tokenNeeded(dataLoadedFromSams?.scope) && (
                    <Grid size={5} alignContent={"start"} offset={2}>
                      <TextField
                        autoFocus
                        required
                        margin="dense"
                        id={`authToken`}
                        label="Token"
                        type="text"
                        fullWidth
                        value={authToken}
                        onChange={(event) => setAuthToken(event.target.value)}
                        error={missingInputIds.includes("authToken")}
                        variant="outlined"
                      />
                    </Grid>
                  )}
                  <Grid size={12}>
                    <Divider variant={"fullWidth"} orientation={"horizontal"} />
                  </Grid>
                  <Grid size={6}>
                    <TeamDataInput
                      teamId={"team1"}
                      title={"Team A"}
                      longName={team1LongName}
                      setLongName={setTeamALongName}
                      shortName={team1ShortName}
                      setShortName={setTeamAShortName}
                      missingInputIds={missingInputIds}
                      logoUrl={team1LogoUrl}
                    />
                  </Grid>
                  <Grid size={6}>
                    <TeamDataInput
                      teamId={"team2"}
                      title={"Team B"}
                      longName={team2LongName}
                      setLongName={setTeamBLongName}
                      shortName={team2ShortName}
                      setShortName={setTeamBShortName}
                      missingInputIds={missingInputIds}
                      logoUrl={team2LogoUrl}
                    />
                  </Grid>
                </>
              )}
            </>
          ) : (
            <>
              <Grid size={4} alignItems={"center"} justifyItems={"center"}>
                <Avatar
                  alt={team1ShortName}
                  src={team1LogoUrl}
                  sx={teamAvatarSx}
                />
              </Grid>
              <Grid size={4} alignContent={"center"} justifyItems={"center"}>
                {millisToDateTime(gameStartDateTime, "long")}
              </Grid>
              <Grid size={4} alignItems={"center"} justifyItems={"center"}>
                <Avatar
                  alt={team2ShortName}
                  src={team2LogoUrl}
                  sx={teamAvatarSx}
                />
              </Grid>
              <Grid size={12}>
                <Divider variant={"middle"} />
              </Grid>
              <Grid size={6}>
                <Button
                  variant={"outlined"}
                  fullWidth
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    py: 2,
                  }}
                  onClick={() => handleSubmit("referee")}
                >
                  <Sports fontSize={"large"} />
                  <Typography>Schiri-Tablet</Typography>
                </Button>
              </Grid>
              <Grid size={6}>
                <Button
                  variant={"outlined"}
                  fullWidth
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    py: 2,
                  }}
                  onClick={() => handleSubmit("challenge")}
                >
                  <Videocam fontSize={"large"} />
                  <Typography>Challenge</Typography>
                </Button>
              </Grid>
            </>
          )}
        </Grid>
      </DialogContent>
      {state === "dataInput" && (
        <DialogActions>
          <DialogActions>
            <Button onClick={handleClose}>Abbrechen</Button>
            <Button disabled={!!emptyInputIds.length} onClick={handleCreate}>
              Erstellen
            </Button>
          </DialogActions>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default CreateGameWizard;
